import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import API_ENDPOINTS from "../config/api";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const GoBack = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  color: #013566;
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

const TablesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 2rem;
`;

const TopTablesRow = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const InfoTile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const InfoTileHeader = styled.div<{ $color?: string }>`
  width: 100%;
  background: ${(props) => props.$color || "#f8f9fa"};
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #eee;
  margin-bottom: 1rem;

  h2 {
    font-size: 1.4rem;
    font-weight: 700;
    color: white;
    margin: 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const InfoTileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #013566;
`;

const FormInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const FormSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const CampaignsTile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const CampaignsHeader = styled.div<{ $color?: string }>`
  width: 100%;
  background: ${(props) => props.$color || "#f8f9fa"};
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #eee;
  margin-bottom: 1rem;

  h2 {
    font-size: 1.4rem;
    font-weight: 700;
    color: white;
    margin: 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const CampaignsHeaderRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #eee;

  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #013566;
    margin: 0;
    flex: 1;
  }
`;

const CampaignsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CampaignsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  gap: 1rem;
  padding: 12px 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 12px 8px;
    margin: 0 -8px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const CampaignsElement = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
`;

const StatusBadge = styled.span<{ $isActive?: boolean }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  background-color: ${(props) => (props.$isActive ? "#4caf50" : "#ffc107")};
  color: white;
`;

const EditButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background-color: transparent;
  color: #013566;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #013566;
    color: white;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const TopButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-bottom: 2rem;
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  padding: 0.75rem 2rem;
  border: ${(props) =>
    props.$variant === "secondary" ? "1px solid #013566" : "none"};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.$variant === "secondary" ? "transparent" : "#013566"};
  color: ${(props) => (props.$variant === "secondary" ? "#013566" : "white")};

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.$variant === "secondary" ? "#f8f9fa" : "#012a4a"};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${month}/${day}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return dateString;
  }
};

const capitalizePaymentType = (type: string | undefined) => {
  if (!type) return "ACH";
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

interface Props {
  setSnackbar: any;
}

const VendorDetails: React.FC<Props> = ({ setSnackbar }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Company Details state
  const [status, setStatus] = useState("Active");
  const [source, setSource] = useState("Direct");
  const [companyName, setCompanyName] = useState("");
  const [dba, setDba] = useState("");
  const [address, setAddress] = useState("");
  const [suite, setSuite] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [dateCreated, setDateCreated] = useState("");
  const [loading, setLoading] = useState(true);

  // Payment Details state
  const [paymentTerms, setPaymentTerms] = useState("");
  const [paymentType, setPaymentType] = useState("ACH");
  const [accountRepresentative, setAccountRepresentative] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [billingEmail, setBillingEmail] = useState("");

  // Campaigns state
  const [campaigns, setCampaigns] = useState<any[]>([]);

  // Save state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchVendorDetails = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await axios.get(API_ENDPOINTS.VENDOR_DETAILS(id), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const vendor = response.data;
        if (vendor) {
          // Company Details
          setDateCreated(formatDate(vendor.created_at));
          setStatus(vendor.status === 1 ? "Active" : "Inactive");
          setSource(vendor.source || "");
          setCompanyName(vendor.company_name || "");
          setDba(vendor.dba || "");
          setAddress(vendor.address || "");
          setSuite(vendor.suite || "");
          setCity(vendor.city || "");
          setState(vendor.state || "");
          setZip(vendor.zip || "");

          // Payment Details
          if (vendor.payment_info) {
            setPaymentTerms(vendor.payment_info.payment_terms || "");
            setPaymentType(
              capitalizePaymentType(vendor.payment_info.payment_type)
            );
            setAccountRepresentative(
              vendor.payment_info.payment_representative || ""
            );
            setAccountEmail(vendor.payment_info.account_email || "");
            setBillingEmail(vendor.payment_info.billing_email || "");
          }

          // Campaigns - check if campaigns exist in the response
          if (vendor.campaigns && Array.isArray(vendor.campaigns)) {
            setCampaigns(vendor.campaigns);
          } else {
            setCampaigns([]);
          }
        }
      } catch (error) {
        console.error("Error fetching vendor details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVendorDetails();
  }, [id, token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) {
      setError("Vendor ID is missing");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Convert status from "Active"/"Inactive" to 1/0
      const statusValue = status === "Active" ? 1 : 0;

      // Convert payment_type to lowercase (API expects lowercase)
      const paymentTypeLower = paymentType.toLowerCase();

      // Prepare request body
      const requestData = {
        status: statusValue,
        source: source,
        company_name: companyName,
        dba: dba || null,
        address: address,
        suite: suite || null,
        city: city,
        state: state,
        zip: zip,
        payment_info: {
          payment_terms: paymentTerms || null,
          payment_type: paymentTypeLower,
          payment_representative: accountRepresentative || null,
          account_email: accountEmail || null,
          billing_email: billingEmail || null,
        },
      };

      await axios.put(API_ENDPOINTS.VENDOR_DETAILS(id), requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setSnackbar({
        isOpen: true,
        message: "Vendor details saved successfully!",
        type: "success",
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      setSnackbar({
        isOpen: true,
        message: "Failed to update vendor details. Please try again.",
        type: "error",
      });
      console.error("Error updating vendor:", err);
      setError(
        err.response?.data?.message ||
          "Failed to update vendor details. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };
  const handleEditCampaign = (campaignId: string) => {
    navigate(`/vendors/${id}/campaigns/${campaignId}/edit`);
  };

  const handleCreateCampaign = () => {
    navigate(`/vendors/${id}/campaigns/create`);
  };

  return (
    <Container>
      <PageTitle>Vendor Details</PageTitle>
      <GoBack onClick={() => navigate("/vendors")}>← Back to Vendors</GoBack>

      <form onSubmit={handleSubmit}>
        {(error || successMessage) && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "1rem",
              borderRadius: "8px",
              backgroundColor: error ? "#fee" : "#efe",
              color: error ? "#c33" : "#3c3",
              border: `1px solid ${error ? "#fcc" : "#cfc"}`,
            }}
          >
            {error || successMessage}
          </div>
        )}

        <TopButtonGroup>
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
          <Button type="button" onClick={handleCreateCampaign}>
            Create Campaign
          </Button>
        </TopButtonGroup>

        <TablesContainer>
          <TopTablesRow>
            {/* Company Details Table */}
            <InfoTile>
              <InfoTileHeader $color="#6B8E6B">
                <h2>Company Details</h2>
              </InfoTileHeader>
              <InfoTileSection>
                <FormGroup>
                  <FormLabel>Date Created</FormLabel>
                  <FormInput type="text" value={dateCreated} readOnly />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="status">Status</FormLabel>
                  <FormSelect
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="source">Source</FormLabel>
                  <FormSelect
                    id="source"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    required
                  >
                    <option value="Direct">Direct</option>
                    <option value="Host & Post">Host & Post</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="companyName">Company Name</FormLabel>
                  <FormInput
                    type="text"
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="dba">DBA</FormLabel>
                  <FormInput
                    type="text"
                    id="dba"
                    value={dba}
                    onChange={(e) => setDba(e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="address">Address</FormLabel>
                  <FormInput
                    type="text"
                    id="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="suite">Suite</FormLabel>
                  <FormInput
                    type="text"
                    id="suite"
                    value={suite}
                    onChange={(e) => setSuite(e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="city">City</FormLabel>
                  <FormInput
                    type="text"
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="state">State</FormLabel>
                  <FormInput
                    type="text"
                    id="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="zip">Zip</FormLabel>
                  <FormInput
                    type="text"
                    id="zip"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    required
                  />
                </FormGroup>
              </InfoTileSection>
            </InfoTile>

            {/* Payment Details Table */}
            <InfoTile>
              <InfoTileHeader $color="#5A7A9A">
                <h2>Payment Details</h2>
              </InfoTileHeader>
              <InfoTileSection>
                <FormGroup>
                  <FormLabel htmlFor="paymentTerms">Payment Terms</FormLabel>
                  <FormInput
                    type="text"
                    id="paymentTerms"
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="paymentType">Payment Type</FormLabel>
                  <FormSelect
                    id="paymentType"
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    required
                  >
                    <option value="ACH">ACH</option>
                    <option value="Credit">Credit</option>
                    <option value="Prepay">Prepay</option>
                    <option value="Paypal">PayPal</option>
                    <option value="Wire">Wire</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="accountRepresentative">
                    Account Representative
                  </FormLabel>
                  <FormInput
                    type="text"
                    id="accountRepresentative"
                    value={accountRepresentative}
                    onChange={(e) => setAccountRepresentative(e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="accountEmail">Account Email</FormLabel>
                  <FormInput
                    type="email"
                    id="accountEmail"
                    value={accountEmail}
                    onChange={(e) => setAccountEmail(e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="billingEmail">Billing Email</FormLabel>
                  <FormInput
                    type="email"
                    id="billingEmail"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                  />
                </FormGroup>
              </InfoTileSection>
            </InfoTile>
          </TopTablesRow>

          {/* Campaigns Table */}
          <CampaignsTile>
            <CampaignsHeader $color="#B8860B">
              <h2>Campaigns</h2>
            </CampaignsHeader>
            <CampaignsHeaderRow>
              <h3>Campaign ID</h3>
              <h3>Campaign Name</h3>
              <h3>Traffic Type</h3>
              <h3>Vertical</h3>
              <h3>Status</h3>
              <h3>Edit</h3>
            </CampaignsHeaderRow>

            <CampaignsSection>
              {loading ? (
                <CampaignsRow>
                  <CampaignsElement
                    style={{ width: "100%", textAlign: "center" }}
                  >
                    Loading campaigns...
                  </CampaignsElement>
                </CampaignsRow>
              ) : campaigns.length > 0 ? (
                campaigns.map((campaign) => (
                  <CampaignsRow key={campaign.id}>
                    <CampaignsElement>{campaign.cid || "N/A"}</CampaignsElement>
                    <CampaignsElement>
                      {campaign.name || campaign.campaign_name || "N/A"}
                    </CampaignsElement>
                    <CampaignsElement>
                      {campaign.traffic_source ||
                        campaign.traffic_type ||
                        "N/A"}
                    </CampaignsElement>
                    <CampaignsElement>
                      {campaign.vertical || "N/A"}
                    </CampaignsElement>
                    <CampaignsElement>
                      <StatusBadge $isActive={campaign.status === 1}>
                        {campaign.status === 1 ? "Active" : "Inactive"}
                      </StatusBadge>
                    </CampaignsElement>
                    <CampaignsElement>
                      <EditButton
                        type="button"
                        onClick={() =>
                          handleEditCampaign(
                            campaign.id || campaign.campaign_id
                          )
                        }
                      >
                        Edit Details
                      </EditButton>
                    </CampaignsElement>
                  </CampaignsRow>
                ))
              ) : (
                <CampaignsRow>
                  <CampaignsElement
                    style={{ width: "100%", textAlign: "center" }}
                  >
                    No campaigns found
                  </CampaignsElement>
                </CampaignsRow>
              )}
            </CampaignsSection>
          </CampaignsTile>
        </TablesContainer>
      </form>
    </Container>
  );
};

export default VendorDetails;
